import React, { useState, useEffect } from 'react';
import { Trash2, CheckCircle } from 'lucide-react';

const FeatureRequests = ({ showToast }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [requestsRes, brandsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/feature-requests`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brands`)
      ]);
      
      const requestsData = await requestsRes.json();
      const brandsData = await brandsRes.json();
      
      setRequests(requestsData);
      setBrands(brandsData);
    } catch (error) {
      showToast('Failed to load feature requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getBrandName = (brandId) => {
    const brand = brands.find(b => b.id === brandId);
    return brand ? brand.name : 'Unknown';
  };

  const handleMarkAsDone = async (requestId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/feature-requests/${requestId}/mark-done`, {
        method: 'PUT'
      });
      
      if (response.ok) {
        showToast('Request marked as done', 'success');
        loadData();
      } else {
        throw new Error('Failed to mark as done');
      }
    } catch (error) {
      showToast('Failed to update request', 'error');
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this feature request?')) {
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/feature-requests/${requestId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        showToast('Request deleted successfully', 'success');
        loadData();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      showToast('Failed to delete request', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-white dark:text-white light:text-slate-900 mb-2">
          💡 Feature Requests
        </h1>
        <p className="text-slate-400 dark:text-slate-400 light:text-slate-600">
          Requests submitted by Brand Managers
        </p>
      </div>

      {requests.length === 0 ? (
        <div className="bg-slate-800 dark:bg-slate-800 light:bg-white rounded-xl p-8 text-center">
          <p className="text-slate-400 dark:text-slate-400 light:text-slate-600">
            No feature requests yet
          </p>
        </div>
      ) : (
        <div className="bg-slate-800 dark:bg-slate-800 light:bg-white rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900 dark:bg-slate-900 light:bg-slate-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 dark:text-slate-400 light:text-slate-700 uppercase tracking-wider">
                    Brand
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 dark:text-slate-400 light:text-slate-700 uppercase tracking-wider">
                    Request
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 dark:text-slate-400 light:text-slate-700 uppercase tracking-wider">
                    Submitted By
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 dark:text-slate-400 light:text-slate-700 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 dark:text-slate-400 light:text-slate-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-slate-400 dark:text-slate-400 light:text-slate-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700 dark:divide-slate-700 light:divide-slate-200">
                {requests.map((request) => (
                  <tr key={request.id} className="hover:bg-slate-700/30 transition-colors">
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-white dark:text-white light:text-slate-900">
                        {getBrandName(request.brand_id)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-white dark:text-white light:text-slate-900 max-w-md">
                        {request.description}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <p className="text-white dark:text-white light:text-slate-900">{request.user_name}</p>
                        <p className="text-slate-400 dark:text-slate-400 light:text-slate-600 text-xs">{request.user_email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-slate-400 dark:text-slate-400 light:text-slate-600">
                        {new Date(request.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span 
                        className={`inline-flex px-3 py-1 rounded-lg text-xs font-semibold ${
                          request.status === 'done'
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}
                      >
                        {request.status === 'done' ? '✓ Done' : '⏳ Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {request.status !== 'done' && (
                          <button
                            onClick={() => handleMarkAsDone(request.id)}
                            className="p-2 rounded-lg hover:bg-green-500/20 text-green-400 transition-colors"
                            title="Mark as Done"
                          >
                            <CheckCircle size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(request.id)}
                          className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-4 text-sm text-slate-500 dark:text-slate-500 light:text-slate-600">
        Total requests: {requests.length}
      </div>
    </div>
  );
};

export default FeatureRequests;
